import { type IReactionDisposer, makeAutoObservable, reaction, runInAction } from 'mobx'
import { AxiosError } from 'axios'
import { logger } from 'shared/lib'
import modalStore from 'shared/ui/Modal/store/modalStore'
import { ModalTypeList } from 'shared/ui/Modal/store/types'
import { toastStore } from 'shared/ui'
import { type IParamsFiltersList } from 'entities/Broadcast/api/types'
import { ContactFiltersApi } from 'entities/Contacts/api/contactsFilters'
import { Segment } from 'entities/Segment/model/Segment'
import { TagsApi } from 'entities/Tags/api/tags'
import { Tag } from 'entities/Tags/model/Tag'
import {
  type IAgentFourthStepPayload,
  type IChatbotFullData,
  type IChatbotPayload,
} from 'entities/Chatbot'
import type { IEntitiesStore } from 'entities/EntitiesExport'
import { Inbox } from 'entities/Inbox/model/Inbox'
import { EnrollmentFilters, EnrollmentStore } from 'widgets/ContactEnrollment'
import { SendFromStore } from 'widgets/SendFrom'
import { SearchTypesEnum } from 'widgets/SearchDropdown'
import type { AgentBookAppointmentStepsStore } from 'pages/chatbot/pages/agentBookAppointment/store/agentBookAppointmentStepsStore'
import { type IHandleError } from 'pages/chatbot/pages/agentBookAppointment/store/type'

export class AgentActivateStore {
  private _modalFreePlanId = 'modal_free_plan'
  private readonly _stepsStore: AgentBookAppointmentStepsStore
  private _enrollmentStore: EnrollmentStore
  private _enrollmentFilters = EnrollmentFilters.Empty()
  private _isLoading = false
  private readonly _numberStore: SendFromStore
  private _errorsMap: Map<string, string> = new Map()
  private _isLoadingActivate = false
  private _disposeIsShowContactsAlert: IReactionDisposer | null = null
  private _disposeContactOwnerReaction: IReactionDisposer | null = null

  constructor(stepsStore: AgentBookAppointmentStepsStore, private _entities: IEntitiesStore) {
    makeAutoObservable(this)

    this._numberStore = new SendFromStore(this._entities, {
      includeRCS: true,
      excludeDeprioritizedNumbers: true,
      getDropdownItemsOptions: {
        showOnlyVerifiedToolFree: true,
      },
      withDynamicSender: this._entities.featureFlagsStore.send_as_contact_owner,
    })
    this._enrollmentStore = new EnrollmentStore(
      {
        filterConfig: {
          makeRequest: () => {
            return ContactFiltersApi.getFilters({
              withCustomFields: true,
              workflowSource: 'chatbot',
              skipValues: ['tag'],
            }).then(({ data }) => data)
          },
        },
        searchDropdownConfig: {
          includeItemsType: [SearchTypesEnum.tags, SearchTypesEnum.contacts],
          placeholder: 'Search tag or contact',
        },
      },
      this._entities
    )
    this.init()
    this._stepsStore = stepsStore
    this.reactionIsShowContactsAlert()
    this.reactionIsUpdateSmartBooking()
  }

  reactionIsShowContactsAlert = () => {
    this._disposeIsShowContactsAlert?.()
    this._disposeIsShowContactsAlert = reaction(
      () => [this._enrollmentStore.isContactReEnroll, this._enrollmentFilters.filters],
      () => this._enrollmentStore.setContactReEnrollAlert(this._enrollmentFilters.filters)
    )
  }

  reactionIsUpdateSmartBooking = () => {
    this._disposeContactOwnerReaction?.()
    this._disposeContactOwnerReaction = reaction(
      () => this.isFromContactOwner,
      (isFromContactOwner) =>
        isFromContactOwner && this._stepsStore.connectCalendarStore.setSmartBookingEnabled(false)
    )
  }

  get enrollmentFilters() {
    return this._enrollmentFilters
  }

  get enrollmentStore() {
    return this._enrollmentStore
  }

  get isLoading() {
    return this._isLoading
  }

  get isLoadingActivate() {
    return this._isLoadingActivate
  }

  get numberStore() {
    return this._numberStore
  }

  get errorsMap() {
    return this._errorsMap
  }

  get disabled() {
    return this._isLoadingActivate
  }

  get isFromContactOwner() {
    return this._numberStore.selectedDropdownItem?.id === 'from_contact_owner'
  }

  get isSendFromInitialized() {
    return this._numberStore.selectedDropdownItem !== null
  }

  get showRcsEmptyFallbackInboxAlert() {
    const inbox = this.numberStore.selectedDropdownItem?.data?.inbox
    const hasFallbackInbox = this._entities.rcsStore.hasFallbackInboxId

    return inbox instanceof Inbox && inbox.isRCS && !hasFallbackInbox
  }

  init = async (data?: IChatbotFullData) => {
    await this._enrollmentStore.filterEditorStore.initFilters()

    if (data) {
      this._numberStore.setSendFrom(data.send_from)
      await this.initContactEnrollment(data?.filters, data?.settings)
    }
  }

  editEnrollment = () => {
    const filters = this._enrollmentFilters.clone()

    const cancelFilters = () => Promise.resolve(true)
    const confirmFilters = async (newFilters: EnrollmentFilters) => {
      this._enrollmentFilters = newFilters
      return true
    }

    this._enrollmentStore.openModal({
      title: 'Contacts',
      allowEmptyCount: true,
      filters,
      action: {
        cancel: cancelFilters,
        confirm: confirmFilters,
      },
    })
  }

  get paramsFilters(): IParamsFiltersList {
    return {
      contacts: this._enrollmentFilters.contacts.map((contact) => contact.id) ?? [],
      segments: this._enrollmentFilters.segments?.map((segment) => segment.id) ?? [],
      tags: this._enrollmentFilters.tags?.map((tag) => tag.id),
      advanced: this._enrollmentFilters.filters?.map((filter) => filter.filtersParams),
    }
  }

  get sendFrom() {
    return this._numberStore.paramsSendFrom
  }

  get stepPayload(): IAgentFourthStepPayload {
    return {
      filters: this.paramsFilters,
      send_from: this.sendFrom,
      settings: {
        allow_re_entering: { value: this._enrollmentStore.isContactReEnroll },
      },
    }
  }

  setError = (key: string, error: string) => {
    this._errorsMap.set(key, error)
  }

  removeError = (key: string) => {
    this._errorsMap.delete(key)
  }

  initContactEnrollment = async (
    filtersListGroups: IChatbotPayload['filters'],
    settings: IChatbotPayload['settings']
  ) => {
    if (!filtersListGroups) return
    try {
      this._isLoading = true
      const tagsIds = filtersListGroups.tags || []
      const contactsIds = filtersListGroups.contacts || []
      const segmentsIds = filtersListGroups.segments || []

      const [tags, contacts] = await Promise.all([
        TagsApi.getTagsById(tagsIds).then((tags) => tags.map((tag) => new Tag(tag))),
        this._entities.contactsStore.getByIds(contactsIds),
      ])
      const segments = segmentsIds.reduce<Segment[]>((state, id) => {
        const item = this._entities.segmentsStore.getItem(id)

        if (item) {
          state.push(item)
        }

        return state
      }, [])

      const advanced = filtersListGroups.advanced?.map(
        (item) =>
          new Segment({
            id: -1,
            organization_id: 0,
            name: '',
            filters: item,
            is_favorite: false,
          })
      )

      this._enrollmentFilters.sync({
        tags: tags,
        segments: segments,
        contacts: contacts,
        filters: advanced,
      })
      this._enrollmentStore.setContactReEnrollAlert(this._enrollmentFilters.filters)
      this._enrollmentStore.setIsContactReEnroll(settings?.allow_re_entering?.value ?? false)
    } catch (e) {
      logger.error(e)
    } finally {
      runInAction(() => {
        this._isLoading = false
      })
    }
  }

  checkValidate = () => {
    this._stepsStore.chatbotStore.setValidateTrigger()
    let validate = true

    if (!this.sendFrom) {
      this.setError('send_from', 'Please select the inbox to send from.')
      validate = false
    }

    if (!this._stepsStore.chatbot.name) {
      this._stepsStore.chatbotErrors.sync({ name: ['Please add a name for your AI Agent.'] })
      validate = false
    }

    return validate
  }

  handleError = (errors: IHandleError) =>
    Object.entries(errors).forEach(([errorKey, errorMessages]) => {
      this.setError(errorKey, Array.isArray(errorMessages) ? errorMessages[0] : errorMessages)
    })

  handleSaveDraft = async (id?: string, shouldRedirect = true) => {
    try {
      await this._stepsStore.quickSaveDraft('step_4', { shouldRedirect, id: Number(id) })
      return true
    } catch (e) {
      this._stepsStore.handleApiError(e, (errors) => this.handleError(errors))
      return false
    }
  }

  openModalFreePlan = (
    id?: string,
    options?: { isPublished?: boolean; shouldRedirect?: boolean }
  ): Promise<boolean> => {
    return new Promise((resolve) => {
      modalStore.addModal({
        disabledOnAllClose: true,
        id: this._modalFreePlanId,
        type: ModalTypeList.WARNING,
        title: 'Free Plan',
        desc: 'For best results, use a paid Calendly account. Free plans don’t confirm bookings to the AI Agent',
        onClose: () => {
          resolve(false)
        },
        primaryAction: {
          text: 'Activate',
          onAction: async () => {
            modalStore.removeModal(this._modalFreePlanId)
            const result = await this.handleActivateClick(id, options)
            resolve(result)
          },
        },
        secondaryAction: {
          text: 'Cancel',
          onAction: () => {
            modalStore.removeModal(this._modalFreePlanId)
            resolve(false)
          },
        },
      })
    })
  }

  handleActivateClickWithModal = async (
    id?: string,
    options?: { isPublished?: boolean; shouldRedirect?: boolean }
  ) => {
    if (!this.checkValidate()) return false
    if (!this._stepsStore.validateKnowledgeBaseDocuments()) return false

    if (this._entities.integrationsStore.calendlyIntegrationStateStore.calendlyStatus === 'free') {
      return await this.openModalFreePlan(id, options)
    } else {
      return await this.handleActivateClick(id, options)
    }
  }

  handleActivateClick = async (
    id?: string,
    options?: { isPublished?: boolean; shouldRedirect?: boolean }
  ) => {
    if (!this.checkValidate()) return false

    runInAction(() => {
      this._isLoadingActivate = true
    })

    try {
      this._stepsStore.syncAllStepsToDirtyData()

      const stepData = this._stepsStore.createStepData('step_4', {
        is_active: true,
      })

      this._stepsStore.setAgentData(stepData)
      this._stepsStore.updateCache()

      await this._stepsStore.handleActivateAgent(Number(id), options)
      return true
    } catch (e) {
      this._stepsStore.handleValidationApiError(e, this.handleError)
      if (e instanceof AxiosError) {
        toastStore.add({
          type: 'error',
          title: 'Something went wrong. Please try again',
        })
      }
      return false
    } finally {
      runInAction(() => {
        this._isLoadingActivate = false
      })
    }
  }

  dispose = () => {
    this._disposeIsShowContactsAlert?.()
    this._disposeContactOwnerReaction?.()
  }
}
